import { Box, Divider, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { initialAppState } from "../../common/reducers/appState";
import { fetchData } from "../../common/services/ApiClient";
import { fileNameFormat, toTitleCase } from "../../common/services/Utilities";
import { AppUrl } from "../../config";
import ResultPagination from "../../containers/ResultPagination";
import SearchPills from "../../containers/SearchPills";
import ShowHideButton from "../ShowHideButton";
import useGetTop100Certs from "../../hooks/useGetTop100Certs";
import {
  Financial,
  KeyStringValue,
  KeyValue,
  Metadata,
  Query,
  SearchPillData,
  SimpleTableRow,
  SortDirection,
  SortProperties,
  Styles,
  TableHeader,
} from "../../interfaces";
import { defaultRegulatoryRegionType } from "../../refdata/constants";
import ApiDataDownload, { apiDownloadProps } from "../ApiDataDownload";
import { RecordCount } from "../BankFind/BankFindResults";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import {
  generateFinancialsApiUrl,
  reportPeriodDateFormat,
  searchTitleLookup,
  top100Banks,
} from "./FinancialReportingUtilities";
import DepositsNameSearchTable from "../../containers/DepositsNameSearchTable";

const PREFIX = "FinancialHeadersMetaData";

const classes = {
  frTableFootnoteFirstLine: `${PREFIX}-frTableFootnoteFirstLine`,
  frTableFootnoteSecondLine: `${PREFIX}-frTableFootnoteSecondLine`,
  loadingSpinner: `${PREFIX}-loadingSpinner`,
  divider: `${PREFIX}-divider`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.frTableFootnoteFirstLine}`]: {
    marginTop: "1.5rem",
    color: theme.palette.primary.main,
    textAlign: "left",
    lineHeight: "1.5rem;",
  },

  [`& .${classes.frTableFootnoteSecondLine}`]: {
    color: theme.palette.primary.main,
    textAlign: "left",
    paddingBottom: 16,
  },

  [`& .${classes.loadingSpinner}`]: { textAlign: "center", marginTop: "2rem" },

  [`& .${classes.divider}`]: {
    backgroundColor: "#1a4480",
    marginTop: ".5rem",
  },
}));

type FinancialApiField = keyof Financial;

interface Props {
  appState: Query;
  institutionType: string;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
  sortProperties: SortProperties;
  clearLatestReportPeriod: () => void;
  removeFilters: (filterValues: string[]) => void;
  removeSearch: (searchValues: string[]) => void;
  updateUi: (uiField: string, uiValue: string[] | undefined) => void;
  tableCheckboxProps?: { header: KeyValue; body: KeyValue; key: string };
  disableTableClick?: boolean;
}

interface FinancialHeaders {
  title: string;
  apiField: FinancialApiField;
  subTitle?: string;
}

export const FinancialHeadersMetaData = (
  searchState: string,
  appState: Query,
) => {
  return [
    {
      title:
        appState && appState.ui.institutionType === "bhcs" ? "BHC ID" : "Cert",
      apiField: `${
        appState && appState.ui.institutionType === "bhcs" ? "RSSDHCR" : "CERT"
      }`,
    },
    {
      title:
        appState && appState.ui.institutionType === "bhcs"
          ? "BHC Name"
          : "Institution Name",
      apiField: `${
        appState && appState.ui.institutionType === "bhcs"
          ? "NAMEHCR"
          : "NAMEFULL"
      }`,
    },
    {
      title: "City",
      apiField: `${
        appState && appState.ui.institutionType === "bhcs" ? "CITYHCR" : "CITY"
      }`,
    },
    {
      title: "State",
      apiField: `${
        appState && appState.ui.institutionType === "bhcs"
          ? "STALPHCR"
          : "STALP"
      }`,
    },
    { title: "Class", apiField: "BKCLASS" },
    {
      title: "Total Assets",
      apiField: "ASSET",
    },
    {
      title:
        searchState && searchState !== "ASSET"
          ? searchTitleLookup(searchState)
          : "Total Domestic Deposits",
      apiField: searchState && searchState !== "ASSET" ? searchState : "DEPDOM",
    },
  ] as FinancialHeaders[];
};

export const FinancialTableHeaders = (appState: Query) => {
  const style = {
    styles: {
      backgroundColor: "#1A4480",
      color: "#FFFFFF",
      textAlign: "left",
      paddingTop: "0px",
      paddingBottom: "0px",
    },
    colSpan: 1,
  };
  return [
    {
      title:
        appState && appState.ui.institutionType === "bhcs" ? "BHC ID" : "Cert",
      ...style,
      apiField: `${
        appState && appState.ui.institutionType === "bhcs" ? "RSSDHCR" : "CERT"
      }`,
    },
    {
      title:
        appState && appState.ui.institutionType === "bhcs"
          ? "BHC Name"
          : "Institution Name",
      ...style,
      apiField: `${
        appState && appState.ui.institutionType === "bhcs"
          ? "NAMEHCR"
          : "NAMEFULL"
      }`,
      colSpan: 1,
    },
    {
      title: "City",
      ...style,
      colSpan: 1,
      apiField: `${
        appState && appState.ui.institutionType === "bhcs" ? "CITYHCR" : "CITY"
      }`,
    },
    {
      title: "State",
      ...style,
      colSpan: 1,
      apiField: `${
        appState && appState.ui.institutionType === "bhcs"
          ? "STALPHCR"
          : "STALP"
      }`,
    },
    {
      title: "Class",
      ...style,
      colSpan: 1,
      apiField: "BKCLASS",
    },
    {
      title: "Total Assets",
      styles: {
        backgroundColor: "#4D73B1",
        color: "#ffffff",
        textAlign: "right",
      },
      colSpan: 1,
      apiField: "ASSET",
    },
    {
      title: "Total Deposits*",
      styles: {
        backgroundColor: "#4D73B1",
        color: "#ffffff",
        textAlign: "right",
      },
      colSpan: 1,
      apiField: "DEPDOM",
    },
  ] as TableHeader[];
};

const FinancialLevelOneHeaders = (tHeader: FinancialHeaders[]) => [
  tHeader.map((header: FinancialHeaders) => {
    const headerStyles: { [key: string]: Styles } = {
      ASSET: { textAlign: "right" },
      DEPDOM: { textAlign: "right" },
      NETINC: { textAlign: "right" },
      NETINCQ: { textAlign: "right" },
    };

    return {
      title: header.title,
      subTitle: header.subTitle,
      styles: headerStyles[header.apiField],
      colSpan: 1,
      apiField: header.apiField,
    };
  }),
];

export function formattedValues(apiField: FinancialApiField, data: Financial) {
  switch (apiField) {
    case "CITY":
      return toTitleCase(data.CITY);
    case "DEPDOM":
    case "NETINC":
    case "NETINCQ":
    case "ASSET":
      return data[apiField] !== null || data[apiField] !== undefined
        ? data?.[apiField]?.toLocaleString()
        : "N/A";
    case "ROAPTX":
    case "ROAPTXQ":
      return Number(data[apiField]).toFixed(2);
    default:
      return data[apiField];
  }
}
const percentageValues = (appState: Query) =>
  appState.filters.financials.includes("ROAPTX");

function rangeSearchPillValue(range: number, appState: Query) {
  return range && !percentageValues(appState) && !isNaN(range) && range < 0
    ? `-$${Number(range)
        .toLocaleString()
        .replace(/-(?=\d)/, "")}`
    : range && !percentageValues(appState) && !isNaN(range) && range >= 0
      ? `$${Number(range).toLocaleString()}`
      : range && percentageValues(appState) && !isNaN(range)
        ? `${Number(range).toLocaleString()}%`
        : "";
}
export const financialReportingSearchPills = (
  appState: Query,
  missingCerts?: string[],
) => {
  let certPills = [];
  if (appState.filters.multiCert && appState.filters.multiCert.length > 0) {
    certPills = appState?.filters?.multiCert.map(
      (cert: string) =>
        !missingCerts?.includes(cert) && {
          value: cert,
          search: false,
          formKey: "multiCert",
          label: "Cert",
        },
    );
  } else if (appState.filters.cert) {
    certPills = [
      {
        value: appState.filters.cert,
        search: false,
        formKey: "cert",
        label: "Cert",
      },
    ];
  }

  return [
    {
      label: !appState.ui.limitInactiveDate ? "Reporting Period" : "",
      value: !appState.ui.limitInactiveDate
        ? reportPeriodDateFormat(appState.filters.reportPeriod?.[0])
        : "",
      search: false,
      static: true,
      formKey: !appState.ui.limitInactiveDate ? "reporting period" : "",
    },
    {
      value:
        appState.ui.limitEstablishedDate &&
        appState.filters.establishedStartRange,
      formKey: "establishedStartRange",
      static: true,
      label: "Established Start Range",
      search: false,
    },
    {
      value:
        appState.ui.limitEstablishedDate &&
        appState.filters.establishedEndRange,
      formKey: "establishedEndRange",
      static: true,
      label: "Established End Range",
      search: false,
    },
    {
      value:
        appState.ui.limitInactiveDate &&
        appState.ui.institutionType === "banks" &&
        appState.filters.inactiveStartRange,
      formKey: "InactiveStartRange",
      static: true,
      label: "Inactive Start Range",
      search: false,
    },
    {
      value:
        appState.ui.limitInactiveDate &&
        appState.ui.institutionType === "banks" &&
        appState.filters.inactiveEndRange,
      formKey: "InactiveEndRange",
      static: true,
      label: "Inactive End Range",
      search: false,
    },
    {
      label: "Name",
      value: appState.search.name,
      formKey: "name",
      search: true,
    },
    {
      label: "BHC Name",
      value: appState.search.bhcName,
      formKey: "bhcName",
      search: true,
    },
    {
      label: "BHC RSSD",
      value: appState.filters.bhcRSSD,
      formKey: "bhcRSSD",
      search: false,
    },
    ...certPills,
    {
      label: appState.filters.financials
        ? `${searchTitleLookup(appState.filters.financials)} - Min`
        : "",
      value: rangeSearchPillValue(appState.filters.minimumRange, appState),
      search: false,
      formKey: "minimumRange",
    },
    {
      label: `${searchTitleLookup(appState.filters.financials)} - Max`,
      value: rangeSearchPillValue(appState.filters.maximumRange, appState),
      search: false,
      formKey: "maximumRange",
    },
    {
      label: "City",
      value:
        appState.ui.institutionType === "banks" || !appState.ui.institutionType
          ? appState.search.city
          : "",
      search: true,
      formKey: "city",
    },
    {
      label: "City",
      value: appState.ui.institutionType === "bhcs" ? appState.search.city : "",
      search: true,
      formKey: "city",
    },
    {
      label: "County",
      value:
        appState.ui.institutionType === "banks" || !appState.ui.institutionType
          ? appState.filters.county
          : "",

      search: false,
      formKey: "county",
    },
    {
      label: "County",
      value:
        appState.ui.institutionType === "bhcs" ? appState.filters.county : "",

      search: false,
      formKey: "county",
    },

    {
      label: "State",
      value:
        appState.ui.institutionType === "banks" || !appState.ui.institutionType
          ? appState.filters.stalp
          : "",
      static: appState.filters.county,
      search: false,
      formKey: "stalp",
    },
    {
      label: "State",
      value:
        appState.ui.institutionType === "bhcs" ? appState.filters.stalp : "",
      static: appState.filters.county,
      search: false,
      formKey: "stalp",
    },
    {
      label: "Zip",
      value: appState.filters.zip,
      search: false,
      formKey: "zip",
    },
    ...regulatorySearchPills(appState),
    ...commonSearchPills(appState),
  ];
};

export const missingCertPills = (missingCerts: string[]) => {
  return missingCerts.map((cert: string) => ({
    value: cert,
    search: false,
    formKey: "multiCert",
    label: "Cert",
  }));
};

export function transformData(
  financialData: Financial[],
  navigate: ReturnType<typeof useNavigate>,
  reportPeriod: string[],
  tHeader: FinancialHeaders[],
  appState: Query,
  noClick?: boolean,
) {
  const tableStylesLookup: { [key: string]: Styles } = {
    ASSET: { textAlign: "right" },
    DEPDOM: { textAlign: "right" },
    NETINC: { textAlign: "right" },
    NETINCQ: { textAlign: "right" },
    ROAPTX: { textAlign: "right" },
    ROAPTXQ: { textAlign: "right" },
    CITY: { textAlign: "left" },
  };
  return financialData.map((data: Financial) => {
    const tableCellData: SimpleTableRow = {};
    tHeader
      .map((header: FinancialHeaders) => header.apiField)
      .forEach((apiField: FinancialApiField) => {
        tableCellData[apiField] = {
          value: formattedValues(apiField, data),
          styles: tableStylesLookup[apiField],
        };
        if (!noClick) {
          tableCellData.rowOnClick = () => {
            const reportUrl = `${AppUrl("FinancialReporting")}/report${window.location.search}`;
            sessionStorage.setItem("prevUrl", reportUrl);
            navigate({
              pathname: `${AppUrl("FinancialReporting")}/details/${data.CERT}`,
              search: appState.ui.limitInactiveDate
                ? "?limitInactiveDate=true"
                : `?reportPeriod=${reportPeriod[0]}`,
            });
          };
        }
      });
    return tableCellData;
  });
}

export function getRegulatoryPillValue(
  appState: Query,
  regulatoryType: string,
) {
  const value = appState.filters[regulatoryType]
    ? defaultRegulatoryRegionType[regulatoryType].filter(
        (regulatoryTypeValue: KeyStringValue) =>
          regulatoryTypeValue.value === appState.filters[regulatoryType],
      )[0].label
    : "";
  return value;
}

export function commonSearchPills(appState: Query): SearchPillData[] {
  const pillValueLookup = (filter: string) => {
    const lookup: KeyStringValue = {
      "INSCOML:1": "Insured Commercial Banks",
      "INSSAVE:1": "Insured Savings Institutions",
      "OI:1": "Insured Branches of Foreign Banks",
      "INSALL:1": "All Insured Institutions",
      "3": "Credit Card Banks",
      "1": "International Banks",
      "2": "Agricultural Banks",
      "4": "Commercial Lenders",
      "5": "Mortgage Lenders",
      "6": "Consumer Lenders",
      "7": "Other Specialized <$1Billion",
      "8": "All Other <$1Billion",
      "9": "All Other >$1Billion",
      "top100": "Top 100 Banks and Thrifts by Assets",
      "OFFFOR:[1 TO *]": "Banks HQ'd in the US with Foreign Offices",
      "PARCERT:[1 TO *]": "Banks Directly Owned by Another Bank ",
      "CB:1": "FDIC Community Banks",
      "FEDCHRTR:1": "Federal Charter",
      "STCHRTR:1": "State Charter",
      "MUTUAL:0 AND INSSAVE:1": "Mutual (non-stock) Savings Institutions",
      "MINORITY:1": "Minority Owned Institutions",
      "MUTUAL:1 AND INSSAVE:1": "Stock Held Savings Institutions",
      "SUBCHAPS:1": "Subchapter S Corporation",
      "TRUST:1": "Trust Powers Granted",
      "TREXER:1": "Trust or Related Activity",
      "SPECGRP:2": "Agricultural Banks",
    };
    return lookup[filter];
  };
  return [
    {
      label: "Charter Type",
      value: pillValueLookup(appState.filters.charterType),
      search: false,
      formKey: "charterType",
    },
    {
      label: "Asset Concentration",
      value: pillValueLookup(appState.filters.assetConcentration),
      search: false,
      formKey: "assetConcentration",
    },
    {
      label: "Common Search",
      value: pillValueLookup(appState.filters.commonSearch),
      search: false,
      formKey: "commonSearch",
    },
  ];
}

export function regulatorySearchPills(appState: Query): SearchPillData[] {
  return [
    {
      label: "Bank Chartering Class",
      value: appState.filters.charterClass,
      search: false,
      formKey: "charterClass",
    },
    {
      label: "Chartering Agency",
      value: appState.filters.charteringAgency,
      search: false,
      formKey: "charteringAgency",
    },
    {
      label: "Regulator",
      value: appState.filters.primaryRegulator,
      search: false,
      formKey: "primaryRegulator",
    },
    {
      label: "Profile",
      value: getRegulatoryPillValue(appState, "profile"),
      search: false,
      formKey: "profile",
    },
    {
      label: "Geographic",
      value: getRegulatoryPillValue(appState, "geographic"),
      search: false,
      formKey: "geographic",
    },
    {
      label: "Supervisory",
      value: getRegulatoryPillValue(appState, "supervisory"),
      search: false,
      formKey: "supervisory",
    },
    {
      label: "Field Office",
      value:
        appState.filters.fieldOffices && appState.filters.fieldOffices === "1"
          ? "ALL"
          : appState.filters.fieldOffices,
      search: false,
      formKey: "fieldOffices",
    },
    {
      label: "MSA",
      value: appState.filters.msa,
      search: false,
      formKey: "msa",
    },
  ];
}

export default function FinancialReportingLevelOne({
  appState,
  sortProperties,
  institutionType,
  updateSort,
  removeFilters,
  removeSearch,
  updateUi,
  tableCheckboxProps,
  disableTableClick,
}: Props) {
  const top100Certs = useGetTop100Certs(appState);
  const [previousAppState, setPreviousAppState] = React.useState({});
  const [financialData, setFinancialData] = React.useState<SimpleTableRow[]>(
    [],
  );
  const [previousInstitutionType, setPreviousInstitutionType] = React.useState(
    institutionType ? institutionType : appState.ui.institutionType,
  );
  const [metaData, setMetaData] = React.useState<Metadata>({
    count: 0,
  });

  const [commonSearch, setCommonSearch] = React.useState(
    appState.filters.commonSearch,
  );
  const navigate = useNavigate();

  const downloadHeaders = React.useMemo(
    () => [
      { label: "Report Period", key: "RISDATE" },
      ...FinancialLevelOneHeaders(
        FinancialHeadersMetaData(appState.filters.financials, appState),
      )[0].map((header: KeyValue) => {
        return {
          label: header.title,
          key: header.apiField,
        };
      }),
    ],
    [appState],
  );

  React.useEffect(() => {
    if (appState.ui.institutionType !== previousInstitutionType) {
      const searchValues =
        appState.ui.institutionType === "banks" ? ["bhcName"] : ["name"];
      const filterValues =
        appState.ui.institutionType === "banks"
          ? ["bhcRSSD"]
          : [
              "cert",
              "multiCert",
              "financials",
              "minimumRange",
              "maximumRange",
              "zip",
              "charterType",
              "assetConcentration",
              "commonSearch",
              "charterClass",
              "charteringAgency",
              "primaryRegulator",
              "profile",
              "geographic",
              "supervisory",
              "fieldOffices",
            ];

      setPreviousInstitutionType(appState.ui.institutionType);
      removeSearch(searchValues);
      removeFilters(filterValues);
    }
  }, [
    appState.ui.institutionType,
    institutionType,
    previousInstitutionType,
    removeFilters,
    removeSearch,
  ]);

  React.useEffect(() => {
    if (appState.ui.minRepDates) {
      updateUi("minRepDates", undefined);
    }
  }, [appState.ui.minRepDates, updateUi]);

  React.useEffect(() => {
    if (appState.filters.commonSearch !== commonSearch) {
      if (top100Banks(appState.filters.commonSearch)) {
        updateSort("ASSET", "DESC");
      }
      setCommonSearch(appState.filters.commonSearch);
    }
  }, [appState.filters.commonSearch, commonSearch, updateSort]);
  const rowsPerPage = appState.pagination.resultLimit
    ? appState.pagination.resultLimit
    : "25";

  const page = appState.pagination.pageNumber
    ? appState.pagination.pageNumber
    : 1;

  const newAppState = React.useMemo(() => {
    return appState.filters.multiCert && appState.filters.multiCert.length === 0
      ? {
          ...appState,
          filters: { ...appState.filters, multiCert: top100Certs },
        }
      : appState;
  }, [appState, top100Certs]);

  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  React.useEffect(() => {
    let isMounted = true;

    if (
      appState.filters.reportPeriod?.[0] &&
      JSON.stringify(previousAppState) !==
        JSON.stringify({
          ...newAppState,
          ui: {
            limitEstablishedDate: appState.ui.limitEstablishedDate,
            limitInactiveDate: appState.ui.limitInactiveDate,
            institutionType: appState.ui.institutionType,
          },
          urls: {},
        })
    ) {
      setPreviousAppState({
        ...newAppState,
        ui: {
          limitEstablishedDate: appState.ui.limitEstablishedDate,
          limitInactiveDate: appState.ui.limitInactiveDate,
          institutionType: appState.ui.institutionType,
        },
        urls: {},
      });
      (async () => {
        setIsFetching(true);
        await fetchData(
          `${generateFinancialsApiUrl(newAppState, [
            ...downloadHeaders.map((header) => header.key),
            appState.ui.institutionType === "bhcs" ? "CERT" : "",
            "MSA",
            "MSA_NAME",
          ])}&sort_by=${
            appState.sort.sortField
          }&sort_order=${appState.sort.sortOrder.toLowerCase()}&limit=${rowsPerPage}&offset=${
            (page - 1) * rowsPerPage
          }`,
        )
          .then((response) => {
            if (isMounted) {
              //certs used to help with checkbox state in application displaying table
              tableCheckboxProps !== undefined &&
                updateUi(
                  "visibleTableCheckboxes",
                  response.data.map((res: KeyValue) => {
                    return res[tableCheckboxProps.key].toString();
                  }),
                );
              setFinancialData(
                transformData(
                  response.data,
                  navigate,
                  appState.filters.reportPeriod,
                  FinancialHeadersMetaData(
                    appState.filters.financials,
                    appState,
                  ),
                  appState,
                  tableCheckboxProps !== undefined || disableTableClick,
                ),
              );
              setMetaData(response.totals);
            }
          })
          .catch((error: Error) => console.error(error))
          .finally(() => {
            isMounted = false;
            setIsFetching(false);
          });
      })();
    }
  }, [
    appState,
    newAppState,
    disableTableClick,
    navigate,
    page,
    previousAppState,
    rowsPerPage,
    tableCheckboxProps,
    top100Certs,
    updateUi,
    downloadHeaders,
  ]);
  const [missingCerts, setMissingCerts] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (appState.filters.multiCert && appState.ui.visibleTableCheckboxes) {
      const searchedCerts = appState.filters.multiCert;
      const validCerts = appState.ui.visibleTableCheckboxes;
      let invalidCerts = searchedCerts.filter(
        (cert: string) => !validCerts.includes(cert) && cert !== "",
      );
      setMissingCerts(invalidCerts);
    }
  }, [
    appState.filters.multiCert,
    appState.ui.visibleTableCheckboxes,
    isFetching,
  ]);
  const initAppState = {
    ...initialAppState.SOD,
    filters: {
      ...initialAppState.SOD.filters,
      lastYear: appState.filters?.reportPeriod?.[0]?.slice(0, 4),
      stalpbr: appState.filters?.stalp,
      zipbr: appState.filters?.zip,
    },
  };
  const tableBody = (
    <SimpleTableBody
      appState={initAppState}
      tableData={financialData}
      tableCheckbox={tableCheckboxProps && tableCheckboxProps.body}
    />
  );

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat("Financials_L1_raw")}.csv`,
      apiPath: "financials",
      text: "Download Results Data - raw",
      downloadUrl: `${generateFinancialsApiUrl(newAppState)}&sort_by=${
        appState.sort.sortField
      }&sort_order=${appState.sort.sortOrder.toLowerCase()}`,
    },
    {
      appState,
      fileName: `${fileNameFormat("Financials_L1_formatted")}.csv`,
      apiPath: "financials",
      text: "Download Results Data - formatted",
      headers: downloadHeaders,
      downloadUrl: `${generateFinancialsApiUrl(newAppState)}&sort_by=${
        appState.sort.sortField
      }&fields=${downloadHeaders.map(
        (header) => header.key,
      )}&sort_order=${appState.sort.sortOrder.toLowerCase()}`,
      footnotes: [
        "*Includes deposits of FDIC-Insured Institutions in domestic offices (50 states and DC).",
        appState.ui.limitInactiveDate === "1994"
          ? "  *When an institution is inactive, financial information was taken from the last reported Call Report or Thrift Financial Report filed by that institution. No financial data is available for institutions that became inactive prior to 1984."
          : null,
      ].filter(Boolean) as string[],
    },
  ];

  const missingCertsTitle = () =>
    missingCerts.length > 1
      ? `${missingCerts.length} Certs not found`
      : `1 Cert not found`;

  const tableHeadersLevelOne: TableHeader[] = [
    {
      title: "",
      styles: {
        backgroundColor: "#1A4480",
        color: "#ffffff",
        paddingBottom: "0px",
      },
      colSpan: 5,
    },
    {
      title: "Values in Thousands USD",
      styles: {
        backgroundColor: "#4D73B1",
        color: "#ffffff",
        fontSize: "12px",
        paddingBottom: "0px",
      },
      colSpan: 2,
    },
  ];
  const allHeaders: TableHeader[][] = [
    tableHeadersLevelOne,
    FinancialTableHeaders(appState),
  ];
  const footer = (
    <Typography variant="body2" className={classes.frTableFootnoteFirstLine}>
      *Includes deposits of FDIC-Insured Institutions in domestic offices (50
      states and DC).
      {appState.ui.limitInactiveDate && (
        <>
          <br />
          *When an institution is inactive, financial information was taken from
          the last reported Call Report or Thrift Financial Report filed by that
          institution. No financial data is available for institutions that
          became inactive prior to 1984.
        </>
      )}
    </Typography>
  );
  return (
    <Root>
      <Box
        id="searchResults"
        sx={{
          color: "primary.main",
          marginTop: -1,
        }}
      >
        <Typography
          component="div"
          style={{
            display: "flex",
            alignItems: "end",
            justifyContent: "space-between",
          }}
        >
          <RecordCount recordCount={metaData.count} />
          <ApiDataDownload
            app="financialReporting"
            apiData={apiData}
            numberOfResults={metaData.count}
          />
        </Typography>
        <Divider />
      </Box>
      <ShowHideButton
        title="Selected Search Criteria"
        body={
          <SearchPills
            dataArray={financialReportingSearchPills(appState, missingCerts)}
            appState={appState}
          />
        }
      />
      {missingCerts && missingCerts.length > 0 && !isFetching && (
        <ShowHideButton
          title={missingCertsTitle()}
          missingCerts={true}
          body={
            <SearchPills
              dataArray={missingCertPills(missingCerts)}
              appState={appState}
              errorColor
            />
          }
        />
      )}

      {appState.ui.institutionType === "bhcs" ? (
        <DepositsNameSearchTable
          isFR
          searchPills={
            <SearchPills
              dataArray={missingCertPills(missingCerts)}
              appState={appState}
              errorColor
            />
          }
        />
      ) : (
        <ResultPagination count={metaData.count}>
          <MultiHeaderTable
            tableBody={tableBody}
            tableHeaders={allHeaders}
            sortProperties={sortProperties}
            headerCheckbox={tableCheckboxProps && tableCheckboxProps.header}
          />
        </ResultPagination>
      )}
      {footer}
    </Root>
  );
}
